import React, { useCallback, useEffect, useMemo, useState } from 'react'
import moment from 'moment'
import Alert from '@mui/material/Alert'
import Autocomplete from '@mui/material/Autocomplete'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Card from '@mui/material/Card'
import Chip from '@mui/material/Chip'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import Divider from '@mui/material/Divider'
import Grid from '@mui/material/Grid'
import LinearProgress from '@mui/material/LinearProgress'
import MenuItem from '@mui/material/MenuItem'
import Stack from '@mui/material/Stack'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import API from '../../../services/API'
import CustomDataGrid from '../../../components/DataGrid/CustomDataGrid'
import OutstandingJourneys from '../components/OutstandingJourneys'
import {
  JOURNEY_TIME_MILESTONES,
  getJourneyStatusTimes,
} from '../journeyWorkflow'

const BAR_HUE = '#0F766E'

const formatMins = (value) =>
  Number.isFinite(value) && value !== null ? `${value} min` : '—'

const formatTime = (value) => (value ? moment(value).format('HH:mm') : '—')

const formatDateTime = (value) =>
  value ? moment(value).format('DD/MM HH:mm') : '—'

const StatTile = ({ label, value, caption }) => (
  <Box display="flex" flex={1} height="100%">
    <Card variant="outlined" sx={{ p: 2, width: '100%' }}>
      <Typography variant="body2" color="text.secondary">
        {label}
      </Typography>
      <Typography sx={{ fontSize: '1.8rem', fontWeight: 800, lineHeight: 1.2 }}>
        {value}
      </Typography>
      {caption && (
        <Typography variant="caption" color="text.secondary">
          {caption}
        </Typography>
      )}
    </Card>
  </Box>
)

// Bar list of average stage durations — a single measure across stages, so
// one hue, values as text ink alongside each bar (per the dataviz method).
const StageDurations = ({ summary }) => {
  const stages = [
    {
      label: 'Mobile → On Scene (time to reach patient)',
      value: summary?.avgResponseMins,
    },
    { label: 'On Scene → With Patient', value: summary?.avgAtPickupMins },
    { label: 'With Patient → Arrived', value: summary?.avgWithPatientMins },
    { label: 'Arrived → Handover Complete', value: summary?.avgHandoverMins },
  ]
  const max = Math.max(...stages.map((s) => s.value || 0), 1)

  return (
    <Box display="flex" flex={1} height="100%">
      <Card variant="outlined" sx={{ p: 2, width: '100%' }}>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          Average stage durations
        </Typography>
        <Stack spacing={1.25} sx={{ mt: 1 }}>
          {stages.map((stage) => (
            <Box key={stage.label}>
              <Stack direction="row" justifyContent="space-between" spacing={1}>
                <Typography variant="caption" color="text.secondary" noWrap>
                  {stage.label}
                </Typography>
                <Typography variant="caption" sx={{ fontWeight: 800 }}>
                  {formatMins(stage.value)}
                </Typography>
              </Stack>
              <Box
                sx={{
                  height: 8,
                  borderRadius: 1,
                  backgroundColor: 'action.hover',
                  overflow: 'hidden',
                }}
              >
                <Box
                  sx={{
                    height: '100%',
                    width: `${((stage.value || 0) / max) * 100}%`,
                    borderRadius: 1,
                    backgroundColor: BAR_HUE,
                  }}
                />
              </Box>
            </Box>
          ))}
        </Stack>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ mt: 1, display: 'block' }}
        >
          Averaged over journeys with recorded status times in the selected
          range.
        </Typography>
      </Card>{' '}
    </Box>
  )
}

const OnTimeChip = ({
  onTime,
  lateLabel = 'Late',
  onTimeLabel = 'On time',
}) => {
  if (onTime === null || onTime === undefined) {
    return (
      <Typography variant="caption" color="text.disabled">
        n/a
      </Typography>
    )
  }
  return (
    <Chip
      size="small"
      label={onTime ? onTimeLabel : lateLabel}
      color={onTime ? 'success' : 'error'}
      variant="outlined"
      sx={{ fontWeight: 700, borderRadius: 1 }}
    />
  )
}

const JourneyTimesDialog = ({ row, onClose }) => (
  <Dialog open={!!row} onClose={onClose} fullWidth maxWidth="sm">
    <DialogTitle sx={{ fontWeight: 800 }}>
      {row?.patientName} — {row?.bookingRef || 'Journey'} times
    </DialogTitle>
    <DialogContent dividers>
      <Stack spacing={1.5}>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          <Chip
            size="small"
            label={row?.status}
            sx={{ borderRadius: 1, fontWeight: 700 }}
          />
          {row?.journeyType && (
            <Chip
              size="small"
              label={row.journeyType}
              sx={{ borderRadius: 1 }}
            />
          )}
          {row?.organisationName && (
            <Chip
              size="small"
              label={row.organisationName}
              sx={{ borderRadius: 1 }}
            />
          )}
          {row?.duty?.dutyRef && (
            <Chip
              size="small"
              label={`Duty ${row.duty.dutyRef}`}
              sx={{ borderRadius: 1 }}
              onClick={() =>
                window.open(
                  `/pts/planning?date=${moment(row.duty.start).format('YYYYMMDD')}`,
                )
              }
            />
          )}
        </Stack>

        <Box>
          <Typography variant="subtitle2" gutterBottom>
            Planned
          </Typography>
          <Typography variant="body2">
            Patient ready from: <b>{formatDateTime(row?.patientReadyFrom)}</b>
          </Typography>
          <Typography variant="body2">
            Target arrival: <b>{formatDateTime(row?.targetArrivalTime)}</b>
          </Typography>
        </Box>

        <Divider />

        <Box>
          <Typography variant="subtitle2" gutterBottom>
            Recorded times
          </Typography>
          {(row?.timeline || []).length ? (
            <Stack spacing={0.5}>
              {row.timeline.map((entry, index) => (
                <Stack
                  key={`${entry.status}-${index}`}
                  direction="row"
                  justifyContent="space-between"
                >
                  <Typography variant="body2">{entry.status}</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 700 }}>
                    {formatDateTime(entry.at)}
                  </Typography>
                </Stack>
              ))}
            </Stack>
          ) : (
            <Typography variant="body2" color="text.secondary">
              No status times recorded for this journey yet.
            </Typography>
          )}
        </Box>

        <Divider />

        <Box>
          <Typography variant="subtitle2" gutterBottom>
            Durations
          </Typography>
          <Stack spacing={0.5}>
            {[
              [
                'Time to reach patient (Mobile → On Scene)',
                row?.durations?.responseMins,
              ],
              [
                'At pickup (On Scene → With Patient)',
                row?.durations?.atPickupMins,
              ],
              ['With patient (→ Arrived)', row?.durations?.withPatientMins],
              ['Handover (Arrived → Complete)', row?.durations?.handoverMins],
              ['Total (Mobile → Handover/Complete)', row?.durations?.totalMins],
            ].map(([label, value]) => (
              <Stack key={label} direction="row" justifyContent="space-between">
                <Typography variant="body2">{label}</Typography>
                <Typography variant="body2" sx={{ fontWeight: 700 }}>
                  {formatMins(value)}
                </Typography>
              </Stack>
            ))}
          </Stack>
        </Box>

        <Stack direction="row" spacing={2}>
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="body2">On time to patient:</Typography>
            <OnTimeChip onTime={row?.kpis?.pickupOnTime} />
          </Stack>
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="body2">On time to destination:</Typography>
            <OnTimeChip onTime={row?.kpis?.arrivalOnTime} />
          </Stack>
        </Stack>
      </Stack>
    </DialogContent>
    <DialogActions>
      {row?.bookingId && (
        <Button onClick={() => window.open(`/pts/bookings/${row.bookingId}`)}>
          View Booking
        </Button>
      )}
      <Button onClick={onClose}>Close</Button>
    </DialogActions>
  </Dialog>
)

const PTSReports = () => {
  const [start, setStart] = useState(
    moment().subtract(30, 'days').format('YYYY-MM-DD'),
  )
  const [end, setEnd] = useState(moment().format('YYYY-MM-DD'))
  const [organisation, setOrganisation] = useState(null)
  const [organisations, setOrganisations] = useState([])
  const [pickupWindowMins, setPickupWindowMins] = useState(60)
  const [arrivalGraceMins, setArrivalGraceMins] = useState(0)
  const [loading, setLoading] = useState(true)
  const [fetchError, setFetchError] = useState(null)
  const [report, setReport] = useState(null)
  const [selectedRow, setSelectedRow] = useState(null)

  useEffect(() => {
    API.get('/pts/organisationoptions')
      .then((res) => {
        if (res.status === 200) setOrganisations(res.data || [])
      })
      .catch(() => {})
  }, [])

  const loadReport = useCallback(() => {
    setLoading(true)
    setFetchError(null)
    const params = new URLSearchParams({
      start,
      end,
      pickupWindowMins: String(pickupWindowMins),
      arrivalGraceMins: String(arrivalGraceMins),
    })
    if (organisation?._id) params.set('organisation', organisation._id)

    API.get(`/pts/reports/journeys?${params.toString()}`)
      .then((res) => {
        if (res.status === 200) {
          setReport(res.data)
        } else {
          setFetchError(
            res.data?.error || 'Unable to load the report. Please try again.',
          )
        }
      })
      .catch((e) =>
        setFetchError(
          e?.response?.data?.error ||
            'Unable to load the report. Please try again.',
        ),
      )
      .finally(() => setLoading(false))
  }, [start, end, organisation, pickupWindowMins, arrivalGraceMins])

  useEffect(() => {
    loadReport()
  }, [loadReport])

  const summary = report?.summary
  const rows = report?.rows || []

  const milestoneColumns = useMemo(
    () =>
      JOURNEY_TIME_MILESTONES.filter(
        (milestone) => milestone.status !== 'Completed',
      ).map((milestone) => ({
        field: `time-${milestone.status}`,
        headerName: milestone.label,
        width: 90,
        sortable: false,
        valueGetter: (value, row) => row?.times?.[milestone.status] || null,
        renderCell: ({ row }) => formatTime(row?.times?.[milestone.status]),
      })),
    [],
  )

  const columns = useMemo(
    () => [
      {
        field: 'anchorTime',
        headerName: 'Date',
        width: 105,
        valueGetter: (value, row) => row?.anchorTime,
        renderCell: ({ row }) => formatDateTime(row?.anchorTime),
      },
      { field: 'bookingRef', headerName: 'Booking', width: 110 },
      { field: 'patientName', headerName: 'Patient', flex: 1, minWidth: 140 },
      {
        field: 'organisationName',
        headerName: 'Organisation',
        flex: 1,
        minWidth: 130,
      },
      { field: 'journeyType', headerName: 'Type', width: 105 },
      { field: 'status', headerName: 'Status', width: 115 },
      ...milestoneColumns,
      {
        field: 'responseMins',
        headerName: 'To Patient',
        width: 95,
        valueGetter: (value, row) => row?.durations?.responseMins,
        renderCell: ({ row }) => formatMins(row?.durations?.responseMins),
      },
      {
        field: 'pickupOnTime',
        headerName: 'Pickup KPI',
        width: 105,
        sortable: false,
        renderCell: ({ row }) => (
          <OnTimeChip onTime={row?.kpis?.pickupOnTime} />
        ),
      },
      {
        field: 'arrivalOnTime',
        headerName: 'Arrival KPI',
        width: 105,
        sortable: false,
        renderCell: ({ row }) => (
          <OnTimeChip onTime={row?.kpis?.arrivalOnTime} />
        ),
      },
    ],
    [milestoneColumns],
  )

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <Typography variant="title2">PTS Reports</Typography>
        <Typography variant="body2" color="text.secondary">
          Journey times, on-time performance, and stage durations. Click a row
          to see every recorded time for a journey.
        </Typography>
        {/* Cross-date by design: outstanding journeys ignore the report's
            date filters so old incomplete work can't drop out of sight. */}
        <OutstandingJourneys sx={{ mt: 1.5 }} />
      </Grid>

      <Grid item xs={12}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={1.5}
          // alignItems={{ md: 'center' }}
        >
          <TextField
            label="From"
            type="date"
            size="small"
            value={start}
            onChange={(e) => setStart(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            label="To"
            type="date"
            size="small"
            value={end}
            onChange={(e) => setEnd(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
          <Autocomplete
            options={organisations}
            getOptionLabel={(option) => option?.orgName || ''}
            value={organisation}
            onChange={(e, value) => setOrganisation(value)}
            isOptionEqualToValue={(option, value) => option?._id === value?._id}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Organisation (all)"
                size="small"
                sx={{ minWidth: 220 }}
              />
            )}
            clearOnEscape
          />
          <TextField
            select
            label="Pickup window"
            size="small"
            value={pickupWindowMins}
            onChange={(e) => setPickupWindowMins(Number(e.target.value))}
            sx={{ minWidth: 140 }}
            helperText="On scene within X min of patient ready"
          >
            {[15, 30, 45, 60, 90, 120].map((mins) => (
              <MenuItem key={mins} value={mins}>
                {mins} min
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            label="Arrival grace"
            size="small"
            value={arrivalGraceMins}
            onChange={(e) => setArrivalGraceMins(Number(e.target.value))}
            sx={{ minWidth: 130 }}
            helperText="Late if past target + grace"
          >
            {[0, 5, 10, 15, 30].map((mins) => (
              <MenuItem key={mins} value={mins}>
                {mins} min
              </MenuItem>
            ))}
          </TextField>
        </Stack>
      </Grid>

      <Grid item xs={12}>
        {loading && <LinearProgress />}
        {fetchError && (
          <Alert severity="error" variant="filled">
            {fetchError}
          </Alert>
        )}
      </Grid>

      {summary && (
        <>
          <Grid item xs={6} sm={4} lg={2}>
            <StatTile
              label="Journeys"
              value={summary.totalJourneys}
              caption={`${summary.completed} completed`}
            />
          </Grid>
          <Grid item xs={6} sm={4} lg={2}>
            <StatTile
              label="Avg time to reach patient"
              value={formatMins(summary.avgResponseMins)}
              caption="Mobile → On Scene"
            />
          </Grid>
          <Grid item xs={6} sm={4} lg={2}>
            <StatTile
              label="On time to patient"
              value={
                summary.pickupKPI.pct === null
                  ? '—'
                  : `${summary.pickupKPI.pct}%`
              }
              caption={`${summary.pickupKPI.onTime}/${summary.pickupKPI.measured} within ${summary.pickupKPI.windowMins} min of ready`}
            />
          </Grid>
          <Grid item xs={6} sm={4} lg={2}>
            <StatTile
              label="On time to destination"
              value={
                summary.arrivalKPI.pct === null
                  ? '—'
                  : `${summary.arrivalKPI.pct}%`
              }
              caption={`${summary.arrivalKPI.onTime}/${summary.arrivalKPI.measured} by target arrival${summary.arrivalKPI.graceMins ? ` (+${summary.arrivalKPI.graceMins} min)` : ''}`}
            />
          </Grid>
          <Grid item xs={6} sm={4} lg={2}>
            <StatTile
              label="Avg total journey"
              value={formatMins(summary.avgTotalMins)}
              caption="Mobile → Handover"
            />
          </Grid>
          <Grid item xs={6} sm={4} lg={2}>
            <StatTile
              label="Exceptions"
              value={summary.cancelled + summary.noShow + summary.aborted}
              caption={`${summary.cancelled} cancelled · ${summary.noShow} no show · ${summary.aborted} aborted`}
            />
          </Grid>

          <Grid item xs={12}>
            <StageDurations summary={summary} />
          </Grid>
        </>
      )}

      <Grid item xs={12}>
        <CustomDataGrid
          rows={rows}
          getRowId={(row) => row._id}
          columns={columns}
          disableRowSelectionOnClick
          onRowClick={({ row }) => setSelectedRow(row)}
          initialState={{
            pagination: { paginationModel: { pageSize: 25 } },
          }}
          pageSizeOptions={[25, 50, 100]}
        />
      </Grid>

      <JourneyTimesDialog
        row={selectedRow}
        onClose={() => setSelectedRow(null)}
      />
    </Grid>
  )
}

export default PTSReports
